import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../utils/axiosSetup.js';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import Swal from 'sweetalert2';
import { closeModal } from '../../../action/modalbehaviour.js';
import { Link } from 'react-router-dom';


const EditProject = ({projectId, list}) => {
    const [projectData, setProjectData] = useState(null);
    const [teamLead, setTeamLead] = useState([]);
    
    const fetchProjects = async (Id) => {      
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/projects/${Id}`)
            .then(response => {
                setProjectData(response.data.data);
                reset(response.data.data);
            })
            .catch(error => {
                console.log(error);
                setProjectData([]); 
            });
    }

    const fetchTeamLead = async () => {
        axiosInstance.get(`${process.env.REACT_APP_API_URL}/users?users=teamleads`)
            .then(response => {
                setTeamLead(response.data.data);
            })
            .catch(error => {
                console.error('Error fetching teamleads', error);
                setTeamLead([]);
            });
    };

    useEffect(() => {
        if (projectId) {
            fetchProjects(projectId);
        }
        fetchTeamLead();
    }, [projectId]);

    // VALIDATION RULES
    const schema = Yup.object().shape({
        title: Yup.string().required('name is a required field'),
        team_lead_id: Yup.string().required('team lead is a required field'),
        start_date: Yup.string().required(),
        amount: Yup.string().when('channel', {
            is: value => value !== 'Upwork',
            then: () => Yup.number().required('budget is a required field').typeError('budget must be a number'),
            otherwise: ()=> Yup.string().nullable().notRequired()
        }),
        description: Yup.string().required()
    });

    const { register, handleSubmit, formState: { errors }, reset} = useForm({
        mode: 'all',
        resolver: yupResolver(schema)
    });
    
    useEffect(() => {
        if (projectData) {
            reset(projectData);
        }
    }, [projectData, reset]);

    const onSubmit = async (data) => {
        const formData = new FormData();
        formData.append('_method', 'patch');
        
        Object.keys(data).forEach(key => {
            if (key === 'files') {
                Array.from(data[key]).forEach((file, index) => {
                    formData.append(`${key}[${index}]`, file);
                });
            }
            else {
                formData.append(key, data[key]);
            }
        });

        axiosInstance.post(`${process.env.REACT_APP_API_URL}/projects/${projectId}`, formData)
            .then(response => {
                Swal.fire({ icon:"success", text:response.data.message});
                closeModal(`edit_project_${projectId}`);
                list();
            })
            .catch(({response:{data}})=>{
                Swal.fire({text:data.message, icon:"error"});
            });
    }

    if (!projectId || !projectData) {
        return null;
    }


    return (
        <div id={`edit_project_${projectId}`} className="modal custom-modal fade" role="dialog">
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Project</h5>
                        <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div className="modal-body">
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="row">
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Channel</label>
                                        <input className="form-control" type="text" value={projectData.channel || ""} {...register('channel')} disabled />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Client</label>
                                        <input className="form-control" type="text" value={projectData.client || ""} disabled />
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Project Name <span className="text-danger">*</span></label>
                                        <input className="form-control" type="text" defaultValue={projectData.title || ""} {...register('title')} />
                                        <div className="text-sm text-danger">
                                            {errors.title && <p>{errors.title.message}</p>}
                                        </div>
                                    </div>
                                </div>                                
                                <div className="col-md-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Team Lead <span className="text-danger">*</span></label>
                                        <select className="form-control" defaultValue={projectData.team_lead_id || ""} {...register('team_lead_id')}>
                                            <option value="">Select Team Lead</option>
                                            {teamLead.map(tl => (
                                                <option key={tl.id} value={tl.id}>{tl.name}</option>
                                            ))}
                                        </select>
                                        <div className="text-sm text-danger">
                                            {errors.team_lead_id && <p>{errors.team_lead_id.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Start Date <span className="text-danger">*</span></label>
                                        <input className="form-control" type="date" defaultValue={projectData.start_date || ""} {...register('start_date')} />
                                        <div className="text-sm text-danger">
                                            {errors.start_date && <p>{errors.start_date.message}</p>}
                                        </div>
                                    </div>
                                </div>
                                {projectData.channel !== 'Upwork' &&
                                    <div className="col-sm-6">
                                        <div className="input-block mb-3">
                                            <label className="col-form-label">Amount <span className="text-danger">*</span></label>
                                            <input className="form-control" type="text" defaultValue={projectData.amount} {...register('amount')} />
                                            <div className="text-sm text-danger">
                                                {errors.amount && <p>{errors.amount.message}</p>}
                                            </div>
                                        </div>
                                    </div>
                                }                                
                                <div className="col-sm-12">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Description <span className="text-danger">*</span></label>
                                        <textarea className="form-control" defaultValue={projectData.description || ""} {...register('description')} rows={3}></textarea>
                                        <div className="text-sm text-danger">
                                            {errors.description && <p>{errors.description.message}</p>}
                                        </div>
                                    </div>
                                </div>                                
                                <div className="col-sm-6">
                                    <div className="input-block mb-3">
                                        <label className="col-form-label">Files</label>
                                        <input className="form-control" type="file" {...register('files')} multiple />                                        
                                    </div>

                                    {projectData.files ? projectData.files.map((file, index) => (
                                        <div key={index}>
                                            <Link to={`${process.env.REACT_APP_API_FILE_URL}/${file}`} className="btn btn-secondary btn-sm mb-3" target="_blank" rel="noopener noreferrer">
                                                Document {++index}
                                            </Link>
                                            <br />
                                        </div>                                        
                                        )) : ""
                                    }
                                </div>
                            </div>
                            <div className="submit-section">
                                <button type="submit" className="btn btn-primary submit-btn">SUBMIT</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditProject